import React, { useState } from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import AddFieldModal from "./AddFieldModal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import NumberInput from "./NumberInput";
import Checkbox from "../../form/Checkbox";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { snakeCase, orderBy } from "lodash";
import { useFormikContext } from "formik";
import getBillingLabel from "../../../utils/billing/getBillingLabel";
import EditLaborRateDialog from "./EditLaborRateDialog";

const styles = {
  headerCell: {
    fontWeight: "semibold",
    fontSize: 16,
    py: "8px",
  },
  cell: {
    fontSize: 16,
    py: "4px",
  },
  returnIcon: {
    mr: 1,
    transform: "rotate(180deg) scaleY(-1)",
  },
};

export default function EditableFieldsTable({
  title,
  baseName,
  fieldsObject,
  unit,
  displayHeader = true,
  readOnly = false,
}) {
  const { setFieldValue } = useFormikContext();
  const [open, setOpen] = React.useState(false);
  const [editingField, setEditingField] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddField = (name, quantity) => {
    const label = name.trim();
    const formattedKey = snakeCase(label);
    setFieldValue(`${baseName}.${formattedKey}`, {
      label,
      rate: quantity,
      inbound_enabled: false,
      outbound_enabled: false,
    });
  };

  const handleInboundChange = key => (fieldName, checked) => {
    if (checked) {
      setFieldValue(`${baseName}.${key}.outbound_enabled`, true);
    }
  };

  const handleEditField = (key) => {
    setEditingField({
      key,
      name: getBillingLabel(key, fieldsObject[key]),
      baseName,
    });
  };

  const handleCloseEditDialog = () => {
    setEditingField(null);
  };

  if (!fieldsObject) return null;

  return (
    <>
      {displayHeader && (
        <TableHead>
          <TableRow>
            <TableCell sx={styles.headerCell}>
              {title}
              {!readOnly && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ ml: 1.5 }}
                  startIcon={<AddIcon />}
                  onClick={handleOpen}
                >
                  Add New
                </Button>
              )}
            </TableCell>
            <TableCell sx={{ ...styles.headerCell }} align="center">
              Inbound
            </TableCell>
            <TableCell sx={{ ...styles.headerCell }} align="center">
              Outbound
            </TableCell>
            <TableCell sx={{ ...styles.headerCell }} align="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Box width={90}>Rate</Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {orderBy(
          Object.entries(fieldsObject),
          [([key, fieldValue]) => (getBillingLabel(key, fieldValue) || "").toLowerCase()]
        ).map(([key, fieldValue]) => (
          <TableRow key={key}>
            <TableCell sx={styles.cell}>
              <Box display="flex" alignItems="center">
                <KeyboardReturnIcon htmlColor="#d5d5d5" sx={styles.returnIcon} />
                {!readOnly ? (
                  <Box
                    component="span"
                    onClick={() => handleEditField(key)}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 16,
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    {getBillingLabel(key, fieldValue)}
                  </Box>
                ) : (
                  <Typography fontSize={16}>{getBillingLabel(key, fieldValue)}</Typography>
                )}
              </Box>
            </TableCell>
            <TableCell sx={styles.cell} align="center">
              <Box display="flex" justifyContent="center">
                <Box width={30}>
                  <Checkbox name={`${baseName}.${key}.inbound_enabled`} label="" sx={{ p: 0.5 }} onChange={handleInboundChange(key)} disabled={readOnly} />
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={styles.cell} align="center">
              <Box display="flex" justifyContent="center">
                <Box width={30}>
                  <Checkbox name={`${baseName}.${key}.outbound_enabled`} label="" sx={{ p: 0.5 }} disabled={readOnly} />
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={styles.cell} align="left">
              <Box display="flex" alignItems="center" gap={1}>
                <Box width={90}>
                  <NumberInput
                    name={`${baseName}.${key}.rate`}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    disabled={readOnly}
                    sx={{
                      "& input[type=number]": {
                        "-moz-appearance": "textfield",
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                  />
                </Box>
                {unit && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
                  >
                    {unit}
                  </Typography>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      {open && !readOnly && <AddFieldModal onClose={handleClose} onAdd={handleAddField} />}
      {editingField && (
        <EditLaborRateDialog
          fieldInfo={editingField}
          onClose={handleCloseEditDialog}
        />
      )}
    </>
  );
}
